<template>
  <div
    id="settings-aside"
    class="z-10 bg-white d-flex w-[230px] min-w-[200px] left-0 overflow-auto"
  >
    <b-nav class="border-bottom-0 d-block w-full pb-[69px]">
      <div
        v-for="(item, i) in routes"
        v-if="item.hasOwnProperty('condition') ? item.condition : item.show"
        :key="`${i}-item`"
      >
        <component
          :is="(!item.route && !item.href) ? 'div' : 'b-link'"
          class="text-decoration-none select-none text-nowrap flex items-center hover:bg-[#F9FBFF] cursor-pointer h-[59px] px-[16px] font-[700] hover:!text-[#44474A] text-[#44474A] text-[15px] w-100 justify-between border-bottom"
          :to="item.route ? {name: item.route, ...item} : null"
          v-bind="{...(item.href && { href: item.href, target: '_blank' })}"
          :active="[item.route, ...item.actives || []].includes($route.name)"
          :class="[{'!text-[#BFC3C7] !cursor-default hover:!text-[#BFC3C7] !bg-[#F8F8F8]': item.locked}, {'bg-[#F9FBFF]': [item.route,...item.actives||[]].includes($route.name) || active === i}]"
          @click="() => item.children ? setActive(i) : null"
        >
          <span
            class="text-truncate inline-flex text-wrap items-center gap-x-[8px] mr-[8px]"
            :class="{'w-[120px]': item.locked}"
          >
            {{ item.title }}
            <span
              v-if="item.badge"
              class="flex items-center justify-center h-[16px] px-[10px] bg-[#3D82EA] gap-x-[4px] rounded-[4px] text-[10px] text-[#FFFFFF] leading-none font-[600] tracking-[1px] uppercase"
            >
              {{ item.badge }}
            </span>
          </span>

          <svg
            v-if="item.children"
            :class="{'rotate-[90deg]': active === i}"
            width="6"
            height="11"
            viewBox="0 0 6 11"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 9.63495L4.85 5.31749L1 1.00003"
              stroke="#6E747A"
              stroke-width="1.5"
            />
          </svg>
          <svg
            v-else-if="item.locked"
            width="13"
            height="14"
            viewBox="0 0 13 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.66667 13L11 13C11.3682 13 11.6667 12.7015 11.6667 12.3333L11.6667 5.66667C11.6667 5.29848 11.3682 5 11 5L1.66667 5C1.29848 5 1 5.29848 1 5.66667L1 12.3333C1 12.7015 1.29848 13 1.66667 13Z"
              stroke="#BFC3C7"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M4.48716 5L8.17947 5C8.26443 5 8.33331 4.93112 8.33331 4.84615L8.33331 3C8.33331 1.89543 7.43788 1 6.33331 1C5.22874 1 4.33331 1.89543 4.33331 3L4.33331 4.84615C4.33331 4.93112 4.40219 5 4.48716 5Z"
              stroke="#BFC3C7"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </component>

        <template v-if="active === i">
          <div
            v-for="(route, j) in item.children"
            v-if="typeof route.show !== 'boolean' || route.show"
            :key="`${j}-child`"
          >
            <component
              :is="route.disabled ? 'div' : 'b-link'"
              v-if="!route.children"
              :to="{name: route.route, ...route}"
              class="text-decoration-none select-none flex items-center hover:bg-[#F9FBFF] cursor-pointer h-[43px] px-[20px] hover:!text-[#44474A] text-[#44474A] text-[14px] w-100 justify-between border-bottom"
              :class="{'!font-[700] !text-[#2C2C2C] bg-[#F9FBFF]': !$route.query || !route.queryKey ? [route.route, ...route.actives || []].includes($route.name) : $route.query[route.queryKey] === route.query[route.queryKey], 'opacity-60': route.disabled}"
            >
              {{ route.title }}
            </component>

            <div
              v-else
              class="border-left border-[#E5E7EB]"
            >
              <button
                class="text-decoration-none select-none flex items-center font-normal hover:bg-[#F9FBFF] cursor-pointer h-[43px] px-[20px] hover:!text-[#44474A] text-[#44474A] text-[14px] w-100 justify-between border-bottom"
                @click="setActiveChild(j)"
              >
                {{ route.title }}

                <svg
                  v-if="route.children"
                  :class="{'rotate-[90deg]': activeChild === j}"
                  width="6"
                  height="11"
                  viewBox="0 0 6 11"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 9.63495L4.85 5.31749L1 1.00003"
                    stroke="#6E747A"
                    stroke-width="1.5"
                  />
                </svg>
              </button>
              <template v-if="activeChild === j">
                <component
                  :is="subRoute.disabled ? 'div' : 'b-link'"
                  v-for="(subRoute, k) in route.children"
                  :key="`${j}-${k}-subchild`"
                  :to="{name: subRoute.route, ...subRoute}"
                  class="first:mt-[43px] text-decoration-none select-none flex items-center hover:bg-[#F9FBFF] cursor-pointer h-[38px] px-[28px] hover:!text-[#44474A] text-[#44474A] text-[13px] w-100 justify-between border-bottom"
                  :class="{'!font-[700] !text-[#2C2C2C] bg-[#F9FBFF]': !$route.query || !subRoute.queryKey ? [subRoute.route,...subRoute.actives||[]].includes($route.name) : $route.query[subRoute.queryKey] === subRoute.query[subRoute.queryKey], 'opacity-60': subRoute.disabled}"
                >
                  {{ subRoute.title }}
                </component>
              </template>
            </div>
          </div>
        </template>
      </div>

      <!--      <b-nav-item :active="$route.name === 'settings.not-found-page'" :to="{name: 'settings.not-found-page'}">Not Found Page</b-nav-item>-->
    </b-nav>
  </div>
</template>

<script>
import {mapGetters, mapState} from 'vuex'

export default {
  name: 'ProjectSettingsNav',
  data () {
    return {
      newAPI: 'https://new-api.estage.com',
      active: null,
      activeChild: null,
      domainVerified: false
    }
  },
  computed: {
    ...mapState('projects', {
      project: state => state.project
    }),
    ...mapState('auth', {
      user: state => state.user
    }),
    ...mapGetters('projects', {
      checkPermissions: 'checkPermissions'
    }),
    awsConnected () {
      return !!this.project.aws_data?.tenant_id
    },
    routes () {
      let routes = []
      const estageRole = this.user.roles.findIndex(role => ['support', 'superadministrator'].includes(role.name))

      if (estageRole !== -1) {
        routes = [
          {
            show: this.checkPermissions('project_settings'),
            title: 'Project Settings',
            children: [
              {
                show: this.checkPermissions('project_settings.general'),
                title: 'General',
                route: 'settings.general',
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.business_details'),
                title: 'Business Details',
                route: 'settings.business-details',
                query: this.$route.query
              },
              // {
              //   title: 'Custom Domain',
              //   route: 'settings.custom-domain',
              //   query: this.$route.query
              // },
              // {
              //   title: 'Cloudflare Enterprise',
              //   route: 'settings.cloudflare-enterprise',
              //   statement: this.user.cf_enterprise_status && this.project.cf_token,
              //   query: this.$route.query
              // },
              {
                show: this.checkPermissions('project_settings.injection'),
                title: 'Code Injection',
                route: 'settings.injection',
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.share'),
                title: 'Share',
                route: 'settings.share',
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.social_share'),
                title: 'Social Share',
                route: 'settings.social_share',
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.seo'),
                title: 'SEO Settings',
                route: 'settings.seo',
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.cookie'),
                title: 'Cookie Consent',
                route: 'settings.cookie',
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.page_restrictions'),
                title: 'Restrictions',
                route: 'settings.page_restrictions',
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.integrations'),
                title: 'Integrations',
                route: 'settings.integrations',
                actives: [
                  'settings.integrations.autoresponders',
                  'settings.integrations.payment-processing',
                  'settings.integrations.marketing',
                  'settings.integrations.analytics',
                  'settings.integrations.chat'
                ],
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.fonts'),
                title: 'Fonts',
                route: 'settings.fonts',
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.recaptcha'),
                title: 'Recaptcha',
                route: 'settings.recaptcha',
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.variables'),
                title: 'Global Variables',
                route: 'settings.variables',
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.global_tracking'),
                title: 'EGT Event Actions',
                route: 'settings.global-tracking',
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.backup'),
                title: 'Backup & Restore',
                route: 'settings.backup',
                query: this.$route.query
              }
            ].filter(link => link.hasOwnProperty('statement') ? link.statement : true)
          },
          {
            show: this.checkPermissions('domain_management'),
            title: 'Domain Management',
            group: 'domain',
            children: this.awsConnected ? [
              {
                title: 'Domains/Sub-domains',
                route: 'domain.management',
                query: this.$route.query
              },
              {
                title: 'DNS Records',
                route: 'domain.records',
                query: this.$route.query
              },
              {
                title: 'DDoS Mitigation',
                route: 'domain.ddos',
                query: this.$route.query
              },
              {
                title: 'Traffic Management',
                route: 'domain.traffic',
                query: this.$route.query
              }
            ] : [
              {
                title: 'Domains/Sub-domains',
                route: 'domain.management',
                query: this.$route.query
              },
              {
                title: 'DNS Records',
                route: 'domain.records',
                query: this.$route.query,
                disabled: true
              },
              {
                title: 'DDoS Mitigation',
                route: 'domain.ddos',
                query: this.$route.query,
                disabled: true
              },
              {
                title: 'Traffic Management',
                route: 'domain.traffic',
                query: this.$route.query,
                disabled: true
              }
            ]
          },
          {
            show: this.checkPermissions('blog'),
            title: 'Blog',
            group: 'settings.blog',
            children: [
              {
                show: this.checkPermissions('blog.posts') && this.checkPermissions('blog.templates') && this.checkPermissions('blog.categories') && this.checkPermissions('blog.authors'),
                title: 'Overview',
                route: 'settings.blog',
                queryKey: 'page',
                query: {
                  page: ''
                }
              },
              {
                show: this.checkPermissions('blog.categories'),
                title: 'Categories',
                route: 'settings.blog',
                queryKey: 'page',
                query: {
                  page: 'categories'
                }
              },
              {
                show: this.checkPermissions('blog.authors'),
                title: 'Authors',
                route: 'settings.blog',
                queryKey: 'page',
                query: {
                  page: 'authors'
                }
              },
              {
                show: this.checkPermissions('blog.posts'),
                title: 'Posts',
                route: 'settings.blog',
                queryKey: 'page',
                query: {
                  page: 'posts'
                }
              },
              {
                show: this.checkPermissions('blog.templates'),
                title: 'Blog Home Templates',
                route: 'settings.blog',
                queryKey: 'page',
                query: {
                  page: 'blog-layouts'
                }
              },
              {
                show: this.checkPermissions('blog.templates'),
                title: 'Post Page Templates',
                route: 'settings.blog',
                queryKey: 'page',
                query: {
                  page: 'post-templates'
                }
              },
              {
                show: this.checkPermissions('blog.templates'),
                title: 'Post Content Templates',
                route: 'settings.blog',
                queryKey: 'page',
                query: {
                  page: 'post-content-templates'
                }
              }
            ]
          },
          {
            show: this.checkPermissions('comments'),
            title: 'Comments',
            route: 'project.comments'
          },
          {
            show: this.checkPermissions('community'),
            title: 'Community',
            route: 'project.community'
          },
          {
            show: this.checkPermissions('crm'),
            title: 'CRM',
            href: `https://crm.estage.com/${this.project.id}`
          },
          {
            show: this.checkPermissions('modal'),
            title: 'Modal Builder',
            route: 'module.modal'
          },
          {
            show: this.checkPermissions('mega_menu'),
            title: 'Mega Menu Builder',
            route: 'module.mega-menu'
          },
          {
            show: this.checkPermissions('marketing_funnels'),
            title: 'Marketing Funnels',
            href: `https://marketing-funnel.estage.com?project=${this.project.id}&owner_id=${this.project.user_id}`
          },
          {
            show: this.checkPermissions('video_hosting'),
            title: 'Video Hosting',
            href: 'https://video.estage.com'
          },
          {
            show: this.checkPermissions('members'),
            title: 'User Management',
            group: 'settings.permissions',
            children: [
              {
                title: 'Roles',
                route: 'settings.permissions.roles',
                actives: [
                  'settings.permissions.roles',
                  'settings.permissions.roles.create',
                  'settings.permissions.roles.create.id'
                ]
              },
              {
                title: 'Users',
                route: 'settings.permissions.users',
                actives: [
                  'settings.permissions.users',
                  'settings.permissions.roles.assign',
                  'settings.permissions.roles.assign.id'
                ]
              }
            ]
          }
          // {
          //   title: 'Affiliates',
          //   locked: true
          // },
          // {
          //   title: 'Contacts',
          //   locked: true
          // },
          // {
          //   title: 'Deadline Redirects',
          //   locked: true
          // },
          // {
          //   title: 'Ecommerce',
          //   locked: true
          // },
          // {
          //   title: 'Interactive Community'
          //   locked: true
          // },
          // {
          //   title: 'Link Shortner',
          //   locked: true
          // },
          // {
          //   title: 'Marketing Automation'
          //   locked: true
          // },
          // {
          //   title: 'Products',
          //   locked: true
          // },
          // {
          //   title: 'Support Desk',
          //   locked: true
          // },
          // {
          //   title: 'Surveys',
          //   locked: true
          // },
          // {
          //   title: 'Webinars',
          //   locked: true
          // }
        ]
      } else {
        routes = [
          {
            show: this.checkPermissions('project_settings'),
            title: 'Project Settings',
            children: [
              {
                show: this.checkPermissions('project_settings.general'),
                title: 'General',
                route: 'settings.general',
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.business_details'),
                title: 'Business Details',
                route: 'settings.business-details',
                query: this.$route.query
              },
              // {
              //   title: 'Cloudflare Enterprise',
              //   route: 'settings.cloudflare-enterprise',
              //   statement: this.user.cf_enterprise_status && this.project.cf_token,
              //   query: this.$route.query
              // },
              {
                show: this.checkPermissions('project_settings.injection'),
                title: 'Code Injection',
                route: 'settings.injection',
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.share'),
                title: 'Share',
                route: 'settings.share',
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.social_share'),
                title: 'Social Share',
                route: 'settings.social_share',
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.seo'),
                title: 'SEO Settings',
                route: 'settings.seo',
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.cookie'),
                title: 'Cookie Consent',
                route: 'settings.cookie',
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.page_restrictions'),
                title: 'Restrictions',
                route: 'settings.page_restrictions',
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.integrations'),
                title: 'Integrations',
                route: 'settings.integrations',
                actives: [
                  'settings.integrations.autoresponders',
                  'settings.integrations.payment-processing',
                  'settings.integrations.marketing',
                  'settings.integrations.analytics',
                  'settings.integrations.chat'
                ],
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.fonts'),
                title: 'Fonts',
                route: 'settings.fonts',
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.recaptcha'),
                title: 'Recaptcha',
                route: 'settings.recaptcha',
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.variables'),
                title: 'Global Variables',
                route: 'settings.variables',
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.global_tracking'),
                title: 'EGT Event Actions',
                route: 'settings.global-tracking',
                query: this.$route.query
              },
              {
                show: this.checkPermissions('project_settings.backup'),
                title: 'Backup & Restore',
                route: 'settings.backup',
                query: this.$route.query
              }
            ].filter(link => link.hasOwnProperty('statement') ? link.statement : true)
          },
          {
            show: this.checkPermissions('domain_management'),
            title: 'Domain Management',
            group: 'domain',
            children: this.awsConnected ? [
              {
                title: 'Domains/Sub-domains',
                route: 'domain.management',
                query: this.$route.query
              },
              {
                title: 'DNS Records',
                route: 'domain.records',
                query: this.$route.query
              },
              {
                title: 'DDoS Mitigation',
                route: 'domain.ddos',
                query: this.$route.query
              },
              {
                title: 'Traffic Management',
                route: 'domain.traffic',
                query: this.$route.query
              }
            ] : [
              {
                title: 'Domains/Sub-domains',
                route: 'domain.management',
                query: this.$route.query
              },
              {
                title: 'DNS Records',
                route: 'domain.records',
                query: this.$route.query,
                disabled: true
              },
              {
                title: 'DDoS Mitigation',
                route: 'domain.ddos',
                query: this.$route.query,
                disabled: true
              },
              {
                title: 'Traffic Management',
                route: 'domain.traffic',
                query: this.$route.query,
                disabled: true
              }
            ]
          },
          {
            show: this.checkPermissions('blog'),
            title: 'Blog',
            group: 'settings.blog',
            children: [
              {
                show: this.checkPermissions('blog.posts') && this.checkPermissions('blog.templates') && this.checkPermissions('blog.categories') && this.checkPermissions('blog.authors'),
                title: 'Overview',
                route: 'settings.blog',
                queryKey: 'page',
                query: {
                  page: ''
                }
              },
              {
                show: this.checkPermissions('blog.categories'),
                title: 'Categories',
                route: 'settings.blog',
                queryKey: 'page',
                query: {
                  page: 'categories'
                }
              },
              {
                show: this.checkPermissions('blog.authors'),
                title: 'Authors',
                route: 'settings.blog',
                queryKey: 'page',
                query: {
                  page: 'authors'
                }
              },
              {
                show: this.checkPermissions('blog.posts'),
                title: 'Posts',
                route: 'settings.blog',
                queryKey: 'page',
                query: {
                  page: 'posts'
                }
              },
              {
                show: this.checkPermissions('blog.templates'),
                title: 'Blog Home Templates',
                route: 'settings.blog',
                queryKey: 'page',
                query: {
                  page: 'blog-layouts'
                }
              },
              {
                show: this.checkPermissions('blog.templates'),
                title: 'Post Page Templates',
                route: 'settings.blog',
                queryKey: 'page',
                query: {
                  page: 'post-templates'
                }
              },
              {
                show: this.checkPermissions('blog.templates'),
                title: 'Post Content Templates',
                route: 'settings.blog',
                queryKey: 'page',
                query: {
                  page: 'post-content-templates'
                }
              }
            ]
          },
          {
            show: this.checkPermissions('comments'),
            title: 'Comments',
            route: 'project.comments'
          },
          {
            show: this.checkPermissions('community'),
            title: 'Community',
            route: 'project.community'
          },
          {
            show: this.checkPermissions('crm'),
            title: 'CRM',
            href: `https://crm.estage.com/${this.project.id}`
          },
          {
            show: this.checkPermissions('modal'),
            title: 'Modal Builder',
            route: 'module.modal'
          },
          {
            show: this.checkPermissions('mega_menu'),
            title: 'Mega Menu Builder',
            route: 'module.mega-menu'
          },
          {
            show: this.checkPermissions('marketing_funnels'),
            title: 'Marketing Funnels',
            href: `https://marketing-funnel.estage.com?project=${this.project.id}&owner_id=${this.project.user_id}`
          },
          {
            show: this.checkPermissions('video_hosting'),
            title: 'Video Hosting',
            href: 'https://video.estage.com'
          },
          {
            show: this.checkPermissions('members'),
            title: 'User Management',
            group: 'settings.permissions',
            children: [
              {
                title: 'Roles',
                route: 'settings.permissions.roles',
                actives: [
                  'settings.permissions.roles',
                  'settings.permissions.roles.create',
                  'settings.permissions.roles.create.id'
                ]
              },
              {
                title: 'Users',
                route: 'settings.permissions.users',
                actives: [
                  'settings.permissions.users',
                  'settings.permissions.roles.assign',
                  'settings.permissions.roles.assign.id'
                ]
              }
            ]
          }
          // {
          //   title: 'Affiliates',
          //   locked: true
          // },
          // {
          //   title: 'Contacts',
          //   locked: true
          // },
          // {
          //   title: 'Deadline Redirects',
          //   locked: true
          // },
          // {
          //   title: 'Ecommerce',
          //   locked: true
          // },
          // {
          //   title: 'Interactive Community'
          //   locked: true
          // },
          // {
          //   title: 'Link Shortner',
          //   locked: true
          // },
          // {
          //   title: 'Marketing Automation'
          //   locked: true
          // },
          // {
          //   title: 'Products',
          //   locked: true
          // },
          // {
          //   title: 'Support Desk',
          //   locked: true
          // },
          // {
          //   title: 'Surveys',
          //   locked: true
          // },
          // {
          //   title: 'Webinars',
          //   locked: true
          // }
        ]
      }

      return routes.filter(link => link.hasOwnProperty('statement') ? link.statement : true)
    }
  },
  methods: {
    checkActive () {
      const currentRoute = this.$route.name

      const groupIndex = this.routes.findIndex(route => {
        if (!route.group) return false
        return currentRoute.startsWith(route.group)
      })

      if (groupIndex !== -1) {
        this.active = groupIndex
        return
      }

      const projectSettingsRoutes = this.routes[0]?.children || []
      const inProjectSettings = projectSettingsRoutes.some(child =>
        [child.route, ...(child.actives || [])].includes(currentRoute)
      )

      if (inProjectSettings) {
        this.active = 0
      }
    },
    setActive (index) {
      if (this.active === index) return this.active = null
      this.active = index
    },
    setActiveChild (index) {
      if (this.activeChild === index) return this.activeChild = null
      this.activeChild = index
    }
  },
  async mounted () {
    this.checkActive()
  }
}
</script>

<style lang="scss" scoped>
#settings-aside {
  height: calc(100vh - 160px);
}

.settings-nav {
  border: 1px solid #E2E5EC;
  border-top-left-radius: 3px;
  border-top-right-radius: 3px;

  .nav-link {
    color: #333333;
    font-weight: 400;
    font-size: 18px;
    line-height: 22px;
    padding: 20px 0 !important;
    margin: 0 20px;

    &.active {
      border: unset !important;
      font-weight: bold;
    }
  }
}
</style>